/**
 * ProposalClient - Client for ProposedAction API operations
 *
 * Provides type-safe operations for managing proposals (list, create, approve, etc.)
 * with comprehensive error handling and org-scoped API access.
 */

import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import {
  parseAPIError,
  ProposalAPIError,
  ProposalNetworkError,
} from './errors';
import type {
  BulkMarkReadRequest,
  BulkMarkReadResponse,
  BulkRejectRequest,
  BulkRejectResponse,
  CreateProposalRequest,
  ProposalDetail,
  ProposalExecutionStatus,
  ProposalListFilters,
  ProposalsListResponse,
  ProposedAction,
  ReassignProposalRequest,
  RejectProposalRequest,
  UpdateClarifyingAnswersRequest,
  UpdatePayloadRequest,
} from './types';

// ============================================================================
// API Helper Functions
// ============================================================================

/**
 * Make an API request with enhanced error handling for proposals.
 * Uses fetchWithDefaults for consistent HTTP behavior.
 */
async function apiCall<T>(
  url: string,
  method: string = 'GET',
  body?: unknown,
  context?: Record<string, unknown>
): Promise<T> {
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  let response: Response;

  try {
    response = await fetchWithDefaults(url, options);
  } catch (error) {
    // Network error (no response received)
    throw new ProposalNetworkError(
      error instanceof Error ? error.message : 'Network request failed',
      context
    );
  }

  // Handle non-2xx responses
  if (!response.ok) {
    let responseBody: unknown;
    try {
      responseBody = await response.json();
    } catch {
      responseBody = await response.text().catch(() => null);
    }

    throw parseAPIError(response.status, responseBody, context);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  // Parse JSON response
  try {
    return await response.json();
  } catch {
    throw new ProposalAPIError('Failed to parse API response', {
      code: 'PARSE_ERROR',
      statusCode: response.status,
      context,
    });
  }
}

// ============================================================================
// ProposalClient Class
// ============================================================================

/**
 * Client for interacting with the ProposedAction API
 */
export class ProposalClient {
  /**
   * Create a new proposal client
   *
   * @param orgId - Organization ID for API requests. Exposed read-only so
   *   callers that pin later work to a proposal's owning org (e.g. progress
   *   polling that must not follow an org switch) can read it back off the
   *   client that created the proposal.
   */
  constructor(public readonly orgId: number) {}

  /**
   * Build base URL for proposals API
   */
  private baseUrl(): string {
    return `/api/orgs/${this.orgId}/proposals`;
  }

  /**
   * List proposals with optional filtering
   *
   * @param filters - Optional filters for the query
   * @returns Paginated list of proposals
   *
   * @example
   * ```typescript
   * const client = new ProposalClient(orgId);
   *
   * // Get all pending proposals
   * const pending = await client.list({ resolution: 'pending' });
   *
   * // Get my assigned proposals
   * const mine = await client.list({ assigned_to_me: true });
   *
   * // Filter by tag
   * const emails = await client.list({ filter_tags__contains: 'email' });
   * ```
   */
  async list(filters?: ProposalListFilters): Promise<ProposalsListResponse> {
    const params = new URLSearchParams();

    if (filters) {
      // Add filters as query parameters
      if (filters.resolution) {
        params.append('resolution', filters.resolution);
      }
      if (filters.assigned_to_me !== undefined) {
        params.append('assigned_to_me', String(filters.assigned_to_me));
      }
      if (filters.filter_tags__contains) {
        params.append('filter_tags__contains', filters.filter_tags__contains);
      }
      if (filters.source_type_name) {
        params.append('source_type_name', filters.source_type_name);
      }
      // Pagination parameters
      if (filters.page !== undefined) {
        params.append('page', String(filters.page));
      }
      if (filters.page_size !== undefined) {
        params.append('page_size', String(filters.page_size));
      }
      // Performance parameter - include expensive recipient data
      if (filters.include_recipient_data) {
        params.append('include_recipient_data', 'true');
      }
    }

    const queryString = params.toString();
    const url = `${this.baseUrl()}/${queryString ? `?${queryString}` : ''}`;

    return apiCall<ProposalsListResponse>(url, 'GET', undefined, {
      operation: 'list',
      filters,
    });
  }

  /**
   * Get a single proposal by ID with full decrypted details
   *
   * @param proposalId - Proposal ID
   * @returns Proposal detail with decrypted actions
   *
   * @example
   * ```typescript
   * const proposal = await client.get(123);
   * console.log(proposal.actions); // Decrypted action payloads
   * ```
   */
  async get(proposalId: number): Promise<ProposalDetail> {
    // Pass ?include=actions,versions so the response embeds version history
    // per action — eliminates the Wave 3 /versions/ fan-out.
    return apiCall<ProposalDetail>(
      `${this.baseUrl()}/${proposalId}/?include=actions,versions`,
      'GET',
      undefined,
      { operation: 'get', proposalId }
    );
  }

  /**
   * Create a new proposal with actions
   *
   * @param request - Proposal creation request
   * @returns Created proposal detail
   *
   * @example
   * ```typescript
   * const proposal = await client.create({
   *   title: 'Send welcome email',
   *   description: 'Automated welcome sequence',
   *   assigned_user_id: 42,
   *   source_type_name: 'ahoy_user_agent_conversation',
   *   source_id: 'conv-123',
   *   actions: [
   *     {
   *       execution_order: 0,
   *       proposed_action_type_name: 'gmail_send',
   *       payload: {
   *         action_params: {
   *           to: ['user@example.com'],
   *           subject: 'Welcome!',
   *           body: 'Thanks for signing up...',
   *         },
   *       },
   *     },
   *   ],
   * });
   * ```
   */
  async create(request: CreateProposalRequest): Promise<ProposalDetail> {
    return apiCall<ProposalDetail>(`${this.baseUrl()}/`, 'POST', request, {
      operation: 'create',
    });
  }

  /**
   * Approve and execute a proposal
   *
   * @param proposalId - Proposal ID
   * @param selectedActionIds - Optional array of action IDs to execute (for selective execution)
   * @param actions - Optional array of action payloads for stale data detection
   * @returns Updated proposal with execution results
   *
   * @example
   * ```typescript
   * // Approve all actions
   * const result = await client.approve(123);
   *
   * // Approve only selected actions
   * const result = await client.approve(123, [1, 2, 3]);
   *
   * // Approve with action payloads for stale data detection
   * const result = await client.approve(123, [1, 2], [
   *   { id: 1, payload: { action_params: { to: ['user@example.com'], ... } } },
   *   { id: 2, payload: { entity_id: '...', ... } }
   * ]);
   *
   * if (result.execution_result?.failed === 0) {
   *   console.log('All actions executed successfully!');
   * }
   * ```
   */
  async approve(
    proposalId: number,
    selectedActionIds?: number[],
    actions?: Array<{ id: number; payload: Record<string, unknown> }>
  ): Promise<ProposalDetail> {
    const body: Record<string, unknown> = {};

    if (selectedActionIds) {
      body.selected_action_ids = selectedActionIds;
    }

    if (actions) {
      body.actions = actions;
    }

    return apiCall<ProposalDetail>(
      `${this.baseUrl()}/${proposalId}/approve/`,
      'POST',
      Object.keys(body).length > 0 ? body : undefined,
      { operation: 'approve', proposalId }
    );
  }

  /**
   * Update action selection for a proposal
   *
   * @param proposalId - Proposal ID
   * @param selectedActionIds - Array of action IDs to select
   * @returns Updated proposal detail
   *
   * @example
   * ```typescript
   * // Select only actions 1 and 2
   * const result = await client.updateSelection(123, [1, 2]);
   * ```
   */
  async updateSelection(
    proposalId: number,
    selectedActionIds: number[]
  ): Promise<ProposalDetail> {
    return apiCall<ProposalDetail>(
      `${this.baseUrl()}/${proposalId}/`,
      'PATCH',
      { selected_action_ids: selectedActionIds },
      { operation: 'updateSelection', proposalId }
    );
  }

  /**
   * Reject a proposal with optional reason
   *
   * @param proposalId - Proposal ID
   * @param request - Rejection request with optional reason
   * @returns Updated proposal
   *
   * @example
   * ```typescript
   * await client.reject(123, {
   *   reason: 'Email content needs revision',
   * });
   * ```
   */
  async reject(
    proposalId: number,
    request: RejectProposalRequest = {}
  ): Promise<ProposalDetail> {
    return apiCall<ProposalDetail>(
      `${this.baseUrl()}/${proposalId}/reject/`,
      'POST',
      request,
      { operation: 'reject', proposalId }
    );
  }

  /**
   * Reopen a dismissed proposal
   *
   * @param proposalId - Proposal ID
   * @returns Updated proposal
   *
   * @example
   * ```typescript
   * await client.reopen(123);
   * ```
   */
  async reopen(proposalId: number): Promise<ProposalDetail> {
    return apiCall<ProposalDetail>(
      `${this.baseUrl()}/${proposalId}/reopen/`,
      'POST',
      {},
      { operation: 'reopen', proposalId }
    );
  }

  /**
   * Reassign a proposal to a different user
   *
   * @param proposalId - Proposal ID
   * @param request - Reassignment request with new assignee
   * @returns Updated proposal
   *
   * @example
   * ```typescript
   * await client.reassign(123, {
   *   new_assignee_user_id: 99,
   * });
   * ```
   */
  async reassign(
    proposalId: number,
    request: ReassignProposalRequest
  ): Promise<ProposalDetail> {
    return apiCall<ProposalDetail>(
      `${this.baseUrl()}/${proposalId}/reassign/`,
      'POST',
      request,
      { operation: 'reassign', proposalId }
    );
  }

  /**
   * Update an action's payload (only for pending proposals)
   *
   * @param proposalId - Proposal ID
   * @param actionId - Action ID
   * @param request - Update request with new payload
   * @returns Updated proposal detail
   *
   * @example
   * ```typescript
   * await client.updateActionPayload(123, 456, {
   *   payload: {
   *     action_params: {
   *       to: ['updated@example.com'],
   *       subject: 'Updated subject',
   *       body: 'Updated body',
   *     },
   *   },
   * });
   * ```
   */
  async updateActionPayload(
    proposalId: number,
    actionId: number,
    request: UpdatePayloadRequest
  ): Promise<ProposedAction> {
    const response = await apiCall<{
      action: ProposedAction;
      proposal: { id: number; title: string; updated_at: string };
    }>(
      `${this.baseUrl()}/${proposalId}/actions/${actionId}/update_payload/`,
      'PUT',
      request,
      { operation: 'updateActionPayload', proposalId, actionId }
    );
    return response.action;
  }

  /**
   * Resolve an action whose execution ended in `indeterminate` state.
   *
   * The backend cannot tell whether the side effect (e.g., Gmail send)
   * actually committed before the worker crashed. The user verifies
   * externally (e.g., checks Gmail Sent folder) and reports the outcome:
   *
   *   - `verified_sent`     → flip to success (executor skips on retry).
   *   - `verified_not_sent` → clear state so the next approve re-executes.
   *
   * @param proposalId - Proposal ID
   * @param actionId - Action ID
   * @param outcome - User-verified outcome
   * @returns The updated action
   */
  async resolveIndeterminate(
    proposalId: number,
    actionId: number,
    outcome: 'verified_sent' | 'verified_not_sent'
  ): Promise<ProposedAction> {
    return apiCall<ProposedAction>(
      `${this.baseUrl()}/${proposalId}/actions/${actionId}/resolve_indeterminate/`,
      'POST',
      { outcome },
      { operation: 'resolveIndeterminate', proposalId, actionId }
    );
  }

  /**
   * Delete a proposal (only pending proposals can be deleted)
   *
   * @param proposalId - Proposal ID
   *
   * @example
   * ```typescript
   * await client.delete(123);
   * ```
   */
  async delete(proposalId: number): Promise<void> {
    return apiCall<undefined>(
      `${this.baseUrl()}/${proposalId}/`,
      'DELETE',
      undefined,
      { operation: 'delete', proposalId }
    );
  }

  /**
   * Start or continue a discussion about a proposal
   *
   * Creates a discussion conversation if needed, then sends a message to it.
   * This starts an AI reasoning chain to iterate on the proposal.
   *
   * @param proposalId - Proposal ID
   * @param message - Message to send to the AI
   * @returns Discussion result with conversation and task details
   *
   * @example
   * ```typescript
   * const result = await client.discuss(123, 'Can you make the email more friendly?');
   * console.log(`Task ${result.task_id} started in conversation ${result.conversation_id}`);
   * ```
   */
  async discuss(
    proposalId: number,
    message: string
  ): Promise<{
    conversation_id: number;
    chain_id: number;
    task_id: string;
    status: string;
    processing_status: string;
  }> {
    const url = `${this.baseUrl()}/${proposalId}/discuss/`;
    logger.debug('[ProposalClient.discuss] Making API call', {
      url,
      orgId: this.orgId,
      proposalId,
      messageLength: message.length,
    });

    try {
      const result = await apiCall(
        url,
        'POST',
        { message },
        { operation: 'discuss', proposalId }
      );
      logger.debug('[ProposalClient.discuss] API call successful', { result });
      return result as {
        conversation_id: number;
        chain_id: number;
        task_id: string;
        status: string;
        processing_status: string;
      };
    } catch (error) {
      logger.error(
        '[ProposalClient.discuss] API call failed',
        error instanceof Error ? error : undefined,
        { component: 'ProposalClient', method: 'discuss' }
      );
      throw error;
    }
  }

  /**
   * Bulk reject all proposals matching filter criteria
   *
   * Rejects all pending proposals assigned to the current user that match
   * the provided filters. Much faster than individual reject calls.
   *
   * @param request - Filter criteria and optional rejection reason
   * @returns Counts of rejected, skipped, and failed proposals
   *
   * @example
   * ```typescript
   * // Reject all email proposals
   * const result = await client.bulkReject({ filter_tags__contains: 'email' });
   * console.log(`Rejected ${result.rejected_count} proposals`);
   *
   * // Reject all proposals (no filter)
   * const result = await client.bulkReject({});
   * ```
   */
  async bulkReject(
    request: BulkRejectRequest = {}
  ): Promise<BulkRejectResponse> {
    return apiCall<BulkRejectResponse>(
      `${this.baseUrl()}/bulk_reject/`,
      'POST',
      request,
      { operation: 'bulkReject', filters: request }
    );
  }

  /**
   * Mark all matching proposals as read
   *
   * Marks all unread proposals assigned to the current user that match
   * the provided filters. Much faster than individual mark-read calls.
   *
   * @param request - Filter criteria
   * @returns Count of proposals marked as read
   *
   * @example
   * ```typescript
   * // Mark all email proposals as read
   * const result = await client.markAllAsRead({ filter_tags__contains: 'email' });
   * console.log(`Marked ${result.marked_count} proposals as read`);
   *
   * // Mark all proposals as read (no filter)
   * const result = await client.markAllAsRead({});
   * ```
   */
  async markAllAsRead(
    request: BulkMarkReadRequest = {}
  ): Promise<BulkMarkReadResponse> {
    return apiCall<BulkMarkReadResponse>(
      `${this.baseUrl()}/bulk_mark_read/`,
      'POST',
      request,
      { operation: 'markAllAsRead', filters: request }
    );
  }

  /**
   * Update clarifying answers for a proposal
   *
   * POSTs the answers array to the backend and returns the full updated
   * ProposalDetail. The `clarifying_questions` field is read-only server-side
   * and cannot be re-fetched via a normal PATCH, so callers should seed the
   * detail cache from this response.
   *
   * @param proposalId - Proposal ID
   * @param request - Answers to persist
   * @returns Updated proposal detail
   */
  async updateClarifyingAnswers(
    proposalId: string | number,
    request: UpdateClarifyingAnswersRequest
  ): Promise<ProposalDetail> {
    return apiCall<ProposalDetail>(
      `${this.baseUrl()}/${proposalId}/clarifying_answers/`,
      'POST',
      request,
      { operation: 'updateClarifyingAnswers', proposalId }
    );
  }

  /**
   * Get execution status for a proposal (lightweight polling endpoint)
   *
   * Returns minimal proposal data for polling execution progress.
   * Much faster than get() since it doesn't decrypt action payloads.
   *
   * @param proposalId - Proposal ID
   * @returns Execution status with resolution and result summary
   *
   * @example
   * ```typescript
   * const status = await client.executionStatus(123);
   * if (status.resolution === 'executing') {
   *   console.log(`${status.execution_result?.successful} of ${status.execution_result?.total_actions} complete`);
   * }
   * ```
   */
  async executionStatus(proposalId: number): Promise<ProposalExecutionStatus> {
    return apiCall<ProposalExecutionStatus>(
      `${this.baseUrl()}/${proposalId}/execution_status/`,
      'GET',
      undefined,
      { operation: 'executionStatus', proposalId, timeout: 15000 }
    );
  }
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create a new proposal client
 *
 * @param orgId - Organization ID
 * @returns ProposalClient instance
 *
 * @example
 * ```typescript
 * const client = createProposalClient(orgId);
 * const proposals = await client.list({ resolution: 'pending' });
 * ```
 */
export function createProposalClient(orgId: number): ProposalClient {
  return new ProposalClient(orgId);
}
